import React from 'react';
import { aggregateByCompany, canAggregate } from './aggregateRows';
import {
  applyRowFilters,
  EMPTY_FILTERS,
  hasUnindexedRows,
  membershipOptions,
  pruneFilters,
  sectorOptions,
} from './rowFilters';
import { applyRowSort, DEFAULT_SORT, fallbackSort, isSortFieldPresent } from './rowSort';
import { attachMovement, buildMovementIndex } from './scoreSeries';
import {
  formatCount,
  formatKnowableAtExact,
  formatTickerChanges,
  inputValueScale,
  knowableRange,
  scoreDeltaToken,
  scoreScale,
  tickerChanges,
} from './format';
import { loadedSpan } from './knowableWindow';
import { tableGrid, visibleCoreColumns, visibleMemberColumns, visibleOptionalColumns } from './columns';
import { INPUT_STATUSES } from '@services/feedView';
import { AGGREGATED_DERIVED_TITLE, previousScoreTitle, scoreDeltaTitle } from './copy';
import { useThemeToken } from './useThemeToken';

// The two columns an aggregated row cannot fill: both describe the occasion before a specific one,
// and an average has no before. Named here so the sort guard and the cells agree on the set.
const DERIVED_SORT_FIELDS = ['prevScore', 'scoreDelta'];

// Everything the table is CURRENTLY SHOWING, and the rules for when that resets.
//
// Six pieces of state and six reset effects, out of a 607-line render body where each rule existed
// only as a comment. They are deliberately asymmetric — different dependency arrays, three of them
// keyed on `result` and one on `feedId` — and getting one wrong hands a new feed the previous
// feed's sort, or an averaged view with nothing on screen saying so. Moved VERBATIM: not one
// dependency array is touched here.
//
// The pipeline order is the feature: filter, then aggregate, then sort, then page. Aggregating
// AFTER the filters is what makes the average describe the period the analyst selected rather than
// the whole read; aggregating BEFORE the sort is what lets a sort order the averages rather than
// the occasions they were made from.
//
// `compact` is a seventh slot with no reset rule at all, and that is deliberate rather than an
// omission: density is a preference about reading, not a fact about the response, so it survives
// everything.

export const PAGE_SIZE = 1000;

// WHEN THE TABLE FORGETS, as one switch.
//
// This was six useState slots and six useEffect resets, each with a deliberately different
// dependency array and each rule stated only as a comment. They are asymmetric on purpose, and the
// asymmetry is the whole subject: three keyed on the result, one on the feed, one on the rows and
// the hidden set, one on the aggregate toggle. Getting one wrong hands a new feed the previous
// feed's sort, or an averaged view with nothing on screen saying so, and none of it throws.
//
// As effects the rules were readable only by reading six dependency arrays and inferring the
// intent. As transitions they are named, adjacent, and impossible to read in the wrong order.
//
// `compact` is a seventh slot with NO rule at all, and the absence is deliberate rather than an
// omission: density is a preference about reading, not a fact about the response, so it appears in
// no reset below and survives everything.
const ACTIONS = {
  // A new response landed. Not the same as a new FEED — see FEED_CHANGED.
  RESULT_ARRIVED: 'RESULT_ARRIVED',
  FEED_CHANGED: 'FEED_CHANGED',
  FILTERS_PATCHED: 'FILTERS_PATCHED',
  FILTERS_CLEARED: 'FILTERS_CLEARED',
  SORTED: 'SORTED',
  // The table checking its own sort against the rows it now has, rather than the analyst pressing.
  SORT_REVALIDATED: 'SORT_REVALIDATED',
  AGGREGATE_SET: 'AGGREGATE_SET',
  COMPACT_SET: 'COMPACT_SET',
  HISTORY_SET: 'HISTORY_SET',
  SHOW_MORE: 'SHOW_MORE',
};

// A fresh result, or a narrower view of one, starts at the first page again. Collapsing the rows
// counts as narrower: page 4 of the occasions is not page 4 of the companies. Applied by every
// transition that narrows, which is what the standalone paging effect used to do one render later.
const restartPaging = (state) => ({ ...state, visibleCount: PAGE_SIZE });

const reducer = (state, action) => {
  switch (action.type) {
    // The aggregate toggle is offered off the FILTERED rows, so one carried into a re-run that can
    // no longer fold anything would vanish from the bar while the table went on averaging. The
    // history panel belongs to the response it was opened from — a drill-down swaps the result in
    // place, and leaving it open would keep a previous feed's occasions above a table that moved on.
    //
    // Filters and sort deliberately SURVIVE: a re-run of the same feed is the same question asked
    // over a different window.
    //
    // RETURNED UNCHANGED when it already holds: this fires on mount and on every landed result, and
    // an always-fresh object costs a render of the table plus a memo comparison for up to a thousand
    // rows. The useState setters this replaced were bailed out by React for free.
    case ACTIONS.RESULT_ARRIVED:
      if (!state.aggregate && state.historyCompanyId === null && state.visibleCount === PAGE_SIZE) return state;
      return restartPaging({ ...state, aggregate: false, historyCompanyId: null });

    // Filters that belonged to ANOTHER feed's rows would silently hide most of these; a sector that
    // does not exist in the new response would hide all of them. That is an argument about the FEED,
    // not about the read, which is why this is a separate transition from the one above and why
    // collapsing the two would wipe the analyst's filters on every re-run. Unchanged when it
    // already holds, for the same reason as above — `EMPTY_FILTERS` is a shared reference precisely
    // so this comparison can be an identity one.
    case ACTIONS.FEED_CHANGED:
      if (state.filters === EMPTY_FILTERS && state.visibleCount === PAGE_SIZE) return state;
      return restartPaging({ ...state, filters: EMPTY_FILTERS });

    case ACTIONS.FILTERS_PATCHED:
      return restartPaging({ ...state, filters: { ...state.filters, ...action.patch } });
    case ACTIONS.FILTERS_CLEARED:
      return restartPaging({ ...state, filters: EMPTY_FILTERS });

    case ACTIONS.SORTED:
      return restartPaging({
        ...state,
        sort: typeof action.sort === 'function' ? action.sort(state.sort) : action.sort,
      });

    // A sort outlives a re-run — re-sorting after every tweak of the window would be tiresome — but
    // only while the column it sorts by is still there.
    //
    // Only the sort the table applied to ITSELF is withdrawn when its column goes. A sort on any
    // other column is one the analyst PRESSED, and hiding its column is their call; reordering the
    // table out from under them would be the larger surprise. An explicit ascending sort on Knowable
    // at survives too — it is dropped when the column goes, not reset to the default's direction.
    //
    // Judged against the movement-attached rows rather than the raw response: the raw rows carry no
    // movementNet, so testing there would drop a Movement sort on every re-run of a feed that can
    // perfectly well be sorted by it.
    case ACTIONS.SORT_REVALIDATED: {
      const { rows, hiddenColumns } = action;
      const prev = state.sort;
      let next;
      if (prev && prev.field === DEFAULT_SORT.field && hiddenColumns.includes(DEFAULT_SORT.field)) next = null;
      else if (prev && isSortFieldPresent(rows, prev.field)) next = prev;
      else next = fallbackSort(rows, hiddenColumns);
      // Paging is untouched: this is the table correcting itself, not the analyst narrowing.
      return next === prev ? state : { ...state, sort: next };
    }

    // Averaging empties Prev score and Change on every row, so a sort by either would tie the whole
    // table and quietly reorder it to nothing. Dropped rather than left to look applied — the header
    // would keep its arrow while the order it claims stopped being real. Aggregation carries
    // knowable-at from the company's latest occasion, so the default is still a real order over the
    // folded rows and is what the dropped sort falls back to.
    case ACTIONS.AGGREGATE_SET: {
      const dropsDerivedSort = action.on && state.sort && DERIVED_SORT_FIELDS.includes(state.sort.field);
      return restartPaging({
        ...state,
        aggregate: action.on,
        sort: dropsDerivedSort ? action.fallback : state.sort,
      });
    }

    // Neither narrows the view, so neither restarts paging.
    case ACTIONS.COMPACT_SET:
      return { ...state, compact: action.on };
    case ACTIONS.HISTORY_SET:
      return { ...state, historyCompanyId: action.companyId };

    case ACTIONS.SHOW_MORE:
      return { ...state, visibleCount: state.visibleCount + PAGE_SIZE };

    default:
      return state;
  }
};

export const useFeedTableView = (result, feedId, hiddenColumns) => {
  const [state, dispatch] = React.useReducer(reducer, undefined, () => ({
    visibleCount: PAGE_SIZE,
    filters: EMPTY_FILTERS,
    // Newest first, rather than the order the engine happened to emit.
    //
    // Resolved here as well as by SORT_REVALIDATED below, and the two cover different moments. This
    // table is mounted once: FeedView renders it with no `key` and useFeedView swaps `result` in
    // place, so a re-run and a drill-down are prop updates, not remounts. The initializer is what
    // the FIRST result of a session paints with — without it that first frame would be server
    // order, then reorder.
    sort: fallbackSort(result?.rows, hiddenColumns),
    // Beside the others rather than inside `filters`, and not by preference. EMPTY_FILTERS is one
    // key per PREDICATE and applyRowFilters ANDs them; this is a view transform that changes the
    // grain of a row rather than deciding whether one survives. Folded in, it would also make
    // isFiltering report the "no rows match these filters" empty state for a toggle that filters
    // nothing.
    aggregate: false,
    compact: false,
    // Which company's score history is open. Here rather than on the page: it is a fact about a row
    // of this table, and the drill-down state the page owns is about which FEED is being shown.
    historyCompanyId: null,
  }));
  const { visibleCount, filters, sort, aggregate, compact, historyCompanyId } = state;

  // Read the same way BaseText reads it: the Change column needs a theme COLOUR, not a token.
  const themeToken = useThemeToken();

  const updateFilters = React.useCallback((patch) => dispatch({ type: ACTIONS.FILTERS_PATCHED, patch }), []);

  // Every company's series, and the scales they share. Built once per READ — never per keystroke —
  // because the line is the whole response by construction: a series built over the filtered rows
  // would empty itself under the missing-scores toggle and would punch window-shaped holes
  // indistinguishable from an unscored occasion. scoreSeries.js states the three failure modes.
  //
  // The geometry is per COMPANY rather than per row, so a company holding forty occasions computes
  // one path, not forty.
  const movement = React.useMemo(() => buildMovementIndex(result?.rows || []), [result]);
  // The net change, on every row, before the filters and before the aggregate. aggregateByCompany
  // spreads the company's latest row, so an aggregated row inherits its company's net and a Movement
  // sort orders both grains without a second derivation.
  const allRows = React.useMemo(() => attachMovement(result?.rows || [], movement), [result, movement]);
  const allMemberColumns = result?.memberColumns || [];
  // Whether this feed COMPOSES anything, which is a different question from whether a row is scored.
  // Several sentences on this page turn on it, and each one that guessed from a null score instead
  // said something untrue about a feed that has no inputs to speak of.
  const hasMemberColumns = allMemberColumns.length > 0;

  const summary = React.useMemo(
    () => ({
      missingCount: allRows.filter((r) => r.score === null).length,
      // The span describes what the read returned, not what survived the filters — it answers "which
      // window did I actually just look at", and a sector filter doesn't change that answer.
      covered: knowableRange(allRows),
      // The same span as `covered`, as the calendar dates the date filter picks in. Here rather than
      // in the control because it answers for the READ: a sector filter doesn't change which days
      // these rows came from, so it must not re-run when one is typed.
      knowableSpan: loadedSpan(allRows),
      // Saturation is judged against the whole response, so filtering doesn't restate what a shade
      // means halfway through reading the table.
      valueScale: inputValueScale(allRows),
      scoreTintScale: scoreScale(allRows),
      // A ticker is resolved as-of each row's knowable_at, so a company that renamed inside the
      // window is one company appearing under two symbols. Said once above the table rather than per
      // row, because the Company column is doing the right thing and only looks like a duplicate.
      // Read over every returned row: a filter that hides one side of a rename doesn't make the
      // rename untrue.
      renamedNote: formatTickerChanges(tickerChanges(allRows)),
      sectors: sectorOptions(allRows),
      memberships: membershipOptions(allRows),
      // Whether the chip for "in none of them" would mean anything. Beside the option lists because
      // it is the same kind of fact — what this response can be asked about — and read off allRows
      // for the same reason: an option that appears and disappears as the view narrows is not an
      // option, it is a moving target.
      hasUnindexed: hasUnindexedRows(allRows),
      // Not one row in the whole response is scored. On a combined feed that is a different fact
      // from "some rows have gaps" and has a different cause, so it is read off the RESPONSE and
      // never off the filtered view — the missing-score toggle makes every visible row unscored by
      // construction, and a note keyed on that would fire the moment the analyst pressed it.
      allUnscored: allRows.length > 0 && allRows.every((r) => r.score === null),
      // Not one company in the response has an earlier occasion to compare against. Read off the
      // RESPONSE for the same reason allUnscored is: the note it drives tells the analyst to widen
      // the knowable window, and a filtered view can be predecessor-free while the read is not.
      // `prevScoreKnowableAt` rather than `prevScore`, because a predecessor that scored nothing is
      // still a predecessor — widening the window would not help that row, and the note must not
      // suggest it would.
      allWithoutPrevious: allRows.length > 0 && allRows.every((r) => !r.prevScoreKnowableAt),
      // Whether any unscored row is carrying a member forward rather than lacking one outright.
      // `members_missing` cannot answer this — the server counts a stale alias as missing — so the
      // per-input status is the only source, and it is what stops the sentence below asserting
      // "missing" over a feed whose members all arrived but arrived unfresh.
      anyStaleOnUnscored: allRows.some(
        (r) => r.score === null && r.members.some((i) => i.status === INPUT_STATUSES.STALE)
      ),
    }),
    [allRows]
  );

  // What the analyst can actually still see and change.
  //
  // Every control in the bar is gated on the current response, but the state they write outlived
  // them: filters reset on a feed CHANGE, not on a new response of the same feed. So a sector chosen
  // against one read could keep narrowing the next with no select on screen to say so.
  //
  // DERIVED rather than written back, deliberately. Pruning the stored filters would discard a
  // choice the analyst made; this only stops an unreachable one from narrowing, so it returns the
  // moment the response carries its vocabulary again.
  const activeFilters = React.useMemo(
    () =>
      pruneFilters(filters, {
        sectors: summary.sectors,
        indexOptions: summary.memberships,
        hasUnindexed: summary.hasUnindexed,
        knowableSpan: summary.knowableSpan,
      }),
    [filters, summary]
  );

  // Filter, then aggregate, then sort. The order is the feature: aggregating AFTER the filters is
  // what makes the average describe the period the analyst selected rather than the whole read, and
  // aggregating BEFORE the sort is what lets a sort order the averages rather than the occasions
  // they were made from. Paging then slices whatever that produced.
  const filteredRows = React.useMemo(() => applyRowFilters(allRows, activeFilters), [allRows, activeFilters]);
  const rows = React.useMemo(
    () => applyRowSort(aggregate ? aggregateByCompany(filteredRows) : filteredRows, sort),
    [filteredRows, aggregate, sort]
  );
  // Read off the FILTERED rows, so the toggle disappears when the current view has nothing to fold —
  // and, once pressed, it has to keep answering for a view whose rows are already one per company.
  const offerAggregate = React.useMemo(() => aggregate || canAggregate(filteredRows), [aggregate, filteredRows]);

  // Everything here describes the READ, not the current view of it: which window it covered, the
  // scales its shades are judged on, which companies renamed inside it, what there is to filter by.
  // None of those answers change when a filter, a sort or the density toggle changes, but each is a
  // full pass over every returned row — and the rename pass parses a Date per row on top of that.
  // Computed inline they all re-ran on every keystroke in the company filter, which on a large feed
  // is the difference between a box that types and one that stalls.

  // The three things that happen TO this table rather than being done to it. Every rule they
  // trigger lives in the reducer above; these only say when.
  React.useEffect(() => {
    dispatch({ type: ACTIONS.RESULT_ARRIVED });
  }, [result]);

  React.useEffect(() => {
    dispatch({ type: ACTIONS.FEED_CHANGED });
  }, [feedId]);

  // The rows this sort would have to order, and the columns still on screen to announce it.
  React.useEffect(() => {
    dispatch({ type: ACTIONS.SORT_REVALIDATED, rows: allRows, hiddenColumns });
  }, [allRows, hiddenColumns]);

  // The columns actually drawn, and the grid they lay out in.
  //
  // Here rather than in the component because the component computes them BELOW its two early
  // returns, where no useMemo can reach them — so they were rebuilt on every render and every prop
  // built from them was a fresh identity. That is what kept FeedTableRow off React.memo.
  const columns = React.useMemo(() => {
    const coreColumns = visibleCoreColumns(hiddenColumns);
    const optionalColumns = visibleOptionalColumns(result, hiddenColumns);
    const memberColumns = visibleMemberColumns(allMemberColumns, hiddenColumns);
    const { template: gridColumns, minWidth: tableMinWidth } = tableGrid(coreColumns, optionalColumns, memberColumns);
    // Keyed lookup for the note guards, which ask whether a column is DRAWN rather than iterating.
    const core = Object.fromEntries(coreColumns.map((column) => [column.key, column]));
    return { core, coreColumns, optionalColumns, memberColumns, gridColumns, tableMinWidth };
  }, [result, hiddenColumns, allMemberColumns]);

  // The wash: a feed with no members is a score feed, where the score IS a member's own value on
  // the member scale. A combined score is left bare — it sits on a different scale from its members,
  // and one colour ramp across both would imply a comparison that isn't there. Slim is why this
  // can't key on the member columns alone: it drops the members from every row, so a combined feed
  // read slim would otherwise be washed on exactly the scale it must never share.
  const washesScore = !!result && !result.slim && !hasMemberColumns;

  // The Change cell's text colour, resolved from the token scoreDeltaToken names. `undefined` for a
  // flat or absent change leaves Body5 on its own default, which is the ordinary text colour.
  const deltaColor = (delta) => themeToken(scoreDeltaToken(delta));

  // The two derived cells render an em dash either way — aggregateByCompany nulls both fields, and
  // formatScore/formatScoreDelta already answer null with a dash. What must NOT carry over is the
  // per-row explanation: previousScoreTitle would tell the analyst to widen the knowable window, and
  // no window would fill a column that an average cannot have.
  const prevScoreTitleFor = (row) =>
    (aggregate ? AGGREGATED_DERIVED_TITLE : previousScoreTitle(row, { knowableFloor: result.knowableFloor })) ||
    undefined;
  const deltaTitleFor = (row) => (aggregate ? AGGREGATED_DERIVED_TITLE : scoreDeltaTitle(row)) || undefined;

  // What an aggregated timestamp is: the latest occasion, and how many it stands for. The count sits
  // beside the instant rather than in a column of its own so the grid keeps its shape as the toggle
  // moves — a table that gains and loses a column under you is harder to read than one that doesn't.
  const occurrenceNote = (row) =>
    row.aggregated && row.occurrences > 1 ? ` · ${formatCount(row.occurrences)} occasions` : '';

  const knowableAtTitleFor = (row) => {
    const exact = formatKnowableAtExact(row.knowableAt);
    if (!row.aggregated || !row.aggregatedSpan || row.occurrences <= 1) return exact || undefined;
    const { from, to } = row.aggregatedSpan;
    const scored =
      row.scoredOccurrences === row.occurrences
        ? ''
        : ` ${formatCount(row.scoredOccurrences)} of them scored; the average is over those.`;
    return `Latest of ${formatCount(row.occurrences)} occasions, ${formatKnowableAtExact(
      from
    )} → ${formatKnowableAtExact(to)}.${scored}`;
  };

  // What a core cell needs and cannot get from its own row: the wash scales, which are facts about
  // the whole response; the movement index; and the tooltip builders, which know the read's
  // knowable floor and whether the view is aggregated. Built once per render and handed down.
  const cellContext = React.useMemo(
    () => ({
      washesScore,
      scoreTintScale: summary.scoreTintScale,
      movement,
      deltaColor,
      prevScoreTitleFor,
      deltaTitleFor,
      knowableAtTitleFor,
      occurrenceNote,
      onOpenHistory: (companyId) => dispatch({ type: ACTIONS.HISTORY_SET, companyId }),
    }),
    [washesScore, summary.scoreTintScale, movement, aggregate, result, themeToken]
  );

  return {
    cellContext,
    columns,
    allMemberColumns,
    hasMemberColumns,
    allRows,
    filteredRows,
    rows,
    summary,
    activeFilters,
    filters,
    sort,
    aggregate,
    compact,
    visibleCount,
    historyCompanyId,
    movement,
    offerAggregate,
    actions: {
      updateFilters,
      clearFilters: () => dispatch({ type: ACTIONS.FILTERS_CLEARED }),
      setSort: (sort) => dispatch({ type: ACTIONS.SORTED, sort }),
      // The fallback is computed here rather than in the reducer: deciding to drop a derived sort is
      // the rule, and what it falls back to is data.
      setAggregate: (on) =>
        dispatch({ type: ACTIONS.AGGREGATE_SET, on, fallback: fallbackSort(result?.rows, hiddenColumns) }),
      setCompact: (on) => dispatch({ type: ACTIONS.COMPACT_SET, on }),
      setHistoryCompanyId: (companyId) => dispatch({ type: ACTIONS.HISTORY_SET, companyId }),
      showMore: () => dispatch({ type: ACTIONS.SHOW_MORE }),
    },
  };
};
